
import {
    defineAsyncComponent,
    onMounted,
    ref,
    watch,
} from 'vue';
import { useStore } from 'vuex';

import useInventarios from '../composables/useInventarios';
import { descripcionConcepto } from '../utils/kardexConceptos';

import numeral from 'numeral';
import ExcelJS from 'exceljs';

const dosDigitos = (n: number) => n.toString().padStart(2, '0');

const formatearFechaDDMMYYYY = (fechaISO: string) => {
    const [anio, mes, dia] = fechaISO.split('-');
    return `${ dia }/${ mes }/${ anio }`;
};

const formatearFechaHoraActual = () => {
    const ahora = new Date();
    return `${ dosDigitos(ahora.getDate()) }/${ dosDigitos(ahora.getMonth() + 1) }/${ ahora.getFullYear() }`
        + ` ${ dosDigitos(ahora.getHours()) }:${ dosDigitos(ahora.getMinutes()) }`;
};

export default {
    components: {
        KardexEditarEntradaSalidaComponent: defineAsyncComponent(
            () => import('@/modules/ventas/components/KardexEditarEntradaSalidaComponent.vue')
        ),
    },
    setup() {
        const store = useStore();

        const {
            obtenerKardexProductos,
            obtenerKardexDia,
        } = useInventarios();

        const headers = [
            { text: 'Producto',        value: 'nombre_producto', sortable: true },
            { text: 'Marca',           value: 'nombre_marca',    sortable: true },
            { text: 'Sd. Inicial',     value: 'saldo_inicial',   sortable: true },
            { text: 'Entradas',        value: 'entradas',        sortable: true },
            { text: 'Salidas',         value: 'salidas',         sortable: true },
            { text: 'Saldo',           value: 'saldo',           sortable: true },
            { text: 'Val. Entradas',   value: 'valor_entradas',  sortable: true },
            { text: 'Costo Salidas',   value: 'costo_salidas',   sortable: true },
            { text: 'Val. Saldo',      value: 'valor_saldo',     sortable: true },
            { text: 'Ingreso Ventas',  value: 'ingreso_ventas',  sortable: true },
            { text: 'Costo Ventas',    value: 'costo_ventas',    sortable: true },
            { text: 'Utilidad Bruta',  value: 'utilidad_bruta',  sortable: true },
        ];

        const headersDetalle = [
            { text: 'Producto',        value: 'nombre_producto',       sortable: true },
            { text: 'Marca',           value: 'nombre_marca',          sortable: true },
            { text: 'Hora',            value: 'hora',                  sortable: true },
            { text: 'Concepto',        value: 'concepto',              sortable: true },
            { text: 'Tipo',            value: 'tipo_movimiento',       sortable: true },
            { text: 'Movimiento',      value: 'movimiento',            sortable: true },
            { text: 'Costo Unitario',  value: 'precio_costo_unitario', sortable: true },
            { text: 'Costo Total',     value: 'costo_total',           sortable: true },
            { text: 'Saldo Unid.',     value: 'saldo_unidades',        sortable: true },
            { text: 'Saldo Valor',     value: 'saldo_valor',           sortable: true },
        ];

        const kardexs        = ref<any[]>([]);
        const movimientosDia = ref<any[]>([]);
        const totalItems     = ref(0);
        const loading        = ref(false);
        const textoFiltro    = ref('');
        const fechaFiltro    = ref('');
        let   searchTimer: any = null;

        const serverOptions = ref({
            page: 1,
            rowsPerPage: 25,
        });

        const kardexEditarEntradaSalidaComponentRef = ref();

        const buscarKardex = async () => {
            loading.value = true;

            if (fechaFiltro.value) {
                const resp = await obtenerKardexDia(fechaFiltro.value, textoFiltro.value);
                loading.value = false;
                if (resp.ok) {
                    movimientosDia.value = resp.data;
                }
                return;
            }

            const resp = await obtenerKardexProductos(
                serverOptions.value.page,
                serverOptions.value.rowsPerPage,
                textoFiltro.value,
            );
            loading.value = false;
            if (resp.ok) {
                kardexs.value    = resp.data.rows;
                totalItems.value = resp.data.total;
            }
        };

        const onFechaChange = () => {
            movimientosDia.value = [];
            serverOptions.value.page = 1;
            buscarKardex();
        };

        const COLUMNAS_EXCEL = [
            { key: 'n',              header: 'N°',              width: 6,  numFmt: undefined },
            { key: 'producto',       header: 'Producto',        width: 35, numFmt: undefined },
            { key: 'marca',          header: 'Marca',           width: 18, numFmt: undefined },
            { key: 'hora',           header: 'Hora',            width: 10, numFmt: undefined },
            { key: 'concepto',       header: 'Concepto',        width: 42, numFmt: undefined },
            { key: 'tipo',           header: 'Tipo',            width: 10, numFmt: undefined },
            { key: 'movimiento',     header: 'Movimiento',      width: 14, numFmt: '#,##0.00' },
            { key: 'costo_unitario', header: 'Costo Unitario',  width: 16, numFmt: '#,##0.00' },
            { key: 'costo_total',    header: 'Costo Total',     width: 14, numFmt: '#,##0.00' },
            { key: 'saldo_unidades', header: 'Saldo Unidades',  width: 16, numFmt: '#,##0.00' },
            { key: 'saldo_valor',    header: 'Saldo Valor',     width: 14, numFmt: '#,##0.00' },
        ];

        const BORDE_FINO = {
            top:    { style: 'thin' as const },
            left:   { style: 'thin' as const },
            bottom: { style: 'thin' as const },
            right:  { style: 'thin' as const },
        };

        const descargarExcel = async () => {
            if (!fechaFiltro.value) return;

            const workbook = new ExcelJS.Workbook();
            workbook.creator = 'MarketBo';
            workbook.created = new Date();

            const hoja = workbook.addWorksheet('Kardex');
            hoja.columns = COLUMNAS_EXCEL.map(({ key, width }) => ({ key, width }));

            const totalColumnas = COLUMNAS_EXCEL.length;
            const sucursal      = store.getters['auth/getSucursal'] || '';
            const nombreUsuario = store.getters['auth/getFullNameUser'] || store.getters['auth/getUserName'] || '';

            hoja.mergeCells(1, 1, 1, totalColumnas);
            const celdaTitulo = hoja.getCell(1, 1);
            celdaTitulo.value = `Kardex - Movimientos del ${ formatearFechaDDMMYYYY(fechaFiltro.value) }`;
            celdaTitulo.font = { bold: true, size: 14 };
            celdaTitulo.alignment = { horizontal: 'center', vertical: 'middle' };
            hoja.getRow(1).height = 24;

            hoja.mergeCells(2, 1, 2, totalColumnas);
            const celdaSucursal = hoja.getCell(2, 1);
            celdaSucursal.value = `Sucursal: ${ sucursal }`;
            celdaSucursal.font = { bold: true, size: 11 };
            celdaSucursal.alignment = { horizontal: 'center', vertical: 'middle' };

            hoja.mergeCells(3, 1, 3, totalColumnas);
            const celdaGeneracion = hoja.getCell(3, 1);
            celdaGeneracion.value = `Generado por: ${ nombreUsuario }    |    Fecha y hora del reporte: ${ formatearFechaHoraActual() }`;
            celdaGeneracion.font = { italic: true, size: 10 };
            celdaGeneracion.alignment = { horizontal: 'center', vertical: 'middle' };

            const filaCabecera = hoja.getRow(5);
            COLUMNAS_EXCEL.forEach((columna, index) => {
                const celda = filaCabecera.getCell(index + 1);
                celda.value = columna.header;
                celda.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                celda.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2F5597' } };
                celda.alignment = { horizontal: 'center', vertical: 'middle' };
                celda.border = BORDE_FINO;
            });

            movimientosDia.value.forEach((item: any, index: number) => {
                const fila = hoja.addRow({
                    n: index + 1,
                    producto: item.nombre_producto,
                    marca: item.nombre_marca,
                    hora: item.hora,
                    concepto: descripcionConcepto(item.concepto),
                    tipo: item.tipo_movimiento === 'entrada' ? 'Entrada' : 'Salida',
                    movimiento: Number(item.movimiento),
                    costo_unitario: Number(item.precio_costo_unitario),
                    costo_total: Number(item.costo_total),
                    saldo_unidades: Number(item.saldo_unidades),
                    saldo_valor: Number(item.saldo_valor),
                });

                COLUMNAS_EXCEL.forEach((columna) => {
                    const celda = fila.getCell(columna.key);
                    celda.border = BORDE_FINO;
                    if (columna.numFmt) celda.numFmt = columna.numFmt;
                });
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });

            const enlace = document.createElement('a');
            enlace.href = URL.createObjectURL(blob);
            enlace.download = 'kardex_' + fechaFiltro.value + '.xlsx';
            document.body.appendChild(enlace);
            enlace.click();
            document.body.removeChild(enlace);
            URL.revokeObjectURL(enlace.href);
        };

        // Cuando cambia página o filas por página
        watch(serverOptions, () => { buscarKardex(); }, { deep: true });

        // Búsqueda con debounce — resetea a página 1
        const onSearch = () => {
            clearTimeout(searchTimer);
            searchTimer = setTimeout(() => {
                serverOptions.value.page = 1;
                buscarKardex();
            }, 350);
        };

        onMounted(async () => {
            await buscarKardex();
        });

        const nuevaEntrada = () => {
            kardexEditarEntradaSalidaComponentRef.value.abrirComponent('entrada');
        };

        const nuevaSalida = () => {
            kardexEditarEntradaSalidaComponentRef.value.abrirComponent('salida');
        };

        const cerrarKardexEditarEntradaSalidaComponentEmit = async (seGrabo: boolean) => {
            if (seGrabo) await buscarKardex();
        };

        return {
            headers,
            headersDetalle,
            textoFiltro,
            fechaFiltro,
            kardexs,
            movimientosDia,
            totalItems,
            loading,
            serverOptions,

            buscarKardex,
            onSearch,
            onFechaChange,
            descargarExcel,
            nuevaEntrada,
            nuevaSalida,

            numeral,
            descripcionConcepto,

            kardexEditarEntradaSalidaComponentRef,
            cerrarKardexEditarEntradaSalidaComponentEmit,
        };
    },
};
